import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { SupportBaseBillingVersion } from "../team";
import {
  automationDetailsSchema,
  billingStatsSchema,
  campaignDetailsSchema,
} from "./itemized-invoice-types";

export enum ServiceInvoiceType {
  MARKETING_SMS = "marketing-sms",
  MARKETING_EMAIL = "marketing-email",
  ORDER_TRACKING = "order-tracking",
  SUPPORT_BASE = "support-base",
  SUPPORT_AI_SUBSCRIPTION = "support-ai-subscription",
  SUPPORT_AI_USAGE = "support-ai-usage",
  SUPPORT_VOICE_SUBSCRIPTION = "support-voice-subscription",
  SUPPORT_VOICE_USAGE = "support-voice-usage",
  /**
   * Usage-based billing for checkout optimization.
   */
  CHECKOUT_OPTIMIZATION = "checkout-optimization",
  /**
   * Subscription billing for checkout optimization.
   */
  CHECKOUT_OPTIMIZATION_SUBSCRIPTION = "checkout-optimization-subscription",
  RETURN_AI_EXCHANGE = "return-ai-exchange",
  RETURNS = "returns",
}

export enum PaymentStatus {
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export enum BillingPeriod {
  MONTHLY = "month",
  YEARLY = "year",
}

export enum CheckoutOptimizationBillingPeriod {
  BI_WEEKLY = "bi-weekly",
}

export const billingPeriodToFriendlyLower: Record<
  BillingPeriod | CheckoutOptimizationBillingPeriod,
  string
> = {
  [BillingPeriod.MONTHLY]: "monthly",
  [BillingPeriod.YEARLY]: "yearly",
  [CheckoutOptimizationBillingPeriod.BI_WEEKLY]: "bi-weekly",
};

export const billingPeriodToFriendlyUpper: Record<
  BillingPeriod | CheckoutOptimizationBillingPeriod,
  string
> = {
  [BillingPeriod.MONTHLY]: "Monthly",
  [BillingPeriod.YEARLY]: "Yearly",
  [CheckoutOptimizationBillingPeriod.BI_WEEKLY]: "Bi-weekly",
};

export function lineItemToString(lineItem: LineItem): string {
  const pricePretty = lineItem.price?.toFixed(2) ?? "0.00";
  if (lineItem.quantity === 1 || lineItem.quantity === null) {
    return `${lineItem.name}: $${pricePretty}`;
  } else {
    return `${lineItem.name} \u00D7 ${lineItem.quantity}: $${pricePretty}`;
  }
}

export type LineItem = z.infer<typeof lineItemSchema>;
const lineItemSchema = z.object({
  name: z.string(),
  /** Price in dollars. Minor units may be longer than 2 decimal places. Null if the line
   * item is not priced.
   *
   * For example, order tracking has three separate credit counts (email, sms, and orders)
   * but only one price per billing period. The price is shown on a null-quantity line item,
   * while the quantities are shown on separate null-price line items.
   */
  price: z
    .number()
    .nullish()
    .transform((val) => val ?? null),
  currency: z.literal(Currency.USD),
  /** Quantity is null for line items that are not quantity-based (e.g. carrier fees) */
  quantity: z
    .number()
    .nullish()
    .transform((val) => val ?? null),
  nameDescription: z.string().optional(),
  quantityDescription: z.string().optional(),
  typeHelper: z.string().optional(),
});

export type BaseServiceInvoice = z.infer<typeof baseServiceInvoiceSchema>;
export const baseServiceInvoiceSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  lineItems: z.array(lineItemSchema),
  billingDueDate: z.date(),
  billedSuccessfully: z.boolean(),
  billingAttempts: z.number(),
  dateOfLastBillingAttempt: z.date().optional().nullable(),
  paymentStatus: createEnum(PaymentStatus, {
    enumName: "PaymentStatus",
  }).nullish(),
  failedPaymentsCount: z.number().default(0).optional(),
});

export type RecurringInvoiceData = z.infer<typeof recurringInvoiceDataSchema>;
const recurringInvoiceDataSchema = z.object({
  invoiceDateStart: z.date(),
  invoiceDateEnd: z.date(),
  periodBillingStart: z.date(),
  periodBillingEnd: z.date(),
});

export type SupportedBillingEngineInvoiceType =
  (typeof supportedBillingEngineInvoiceTypes)[number];
export const supportedBillingEngineInvoiceTypes = [
  ServiceInvoiceType.MARKETING_EMAIL,
  ServiceInvoiceType.MARKETING_SMS,
  ServiceInvoiceType.ORDER_TRACKING,
  ServiceInvoiceType.RETURN_AI_EXCHANGE,
  ServiceInvoiceType.RETURNS,
  ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION,
] as const satisfies ServiceInvoiceType[];
export function isSupportedBillingEngineInvoiceType(
  invoiceType: ServiceInvoiceType,
): invoiceType is SupportedBillingEngineInvoiceType {
  return supportedBillingEngineInvoiceTypes.includes(
    invoiceType as SupportedBillingEngineInvoiceType,
  );
}

export const baseBillingEngineInvoiceSchema = baseServiceInvoiceSchema.extend({
  kind: createEnum(supportedBillingEngineInvoiceTypes, {
    enumName: "SupportedBillingEngineInvoiceType",
  }),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
});
export type BaseBillingEngineInvoice = z.infer<
  typeof baseBillingEngineInvoiceSchema
>;

export type MarketingSmsInvoice = z.infer<typeof marketingSmsInvoiceSchema>;
export const marketingSmsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.MARKETING_SMS),
  usageBillingWindowConfigId: zExt.objectId().optional(),
  breakdowns: z
    .object({
      automations: z.array(automationDetailsSchema),
      campaigns: z.array(campaignDetailsSchema),
      inbound: billingStatsSchema.optional(),
    })
    .optional(),
});

export type MarketingEmailInvoice = z.infer<typeof marketingEmailInvoiceSchema>;
export const marketingEmailInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.MARKETING_EMAIL),
    usageBillingWindowConfigId: zExt.objectId().optional(),
    breakdowns: z
      .object({
        automations: z.array(automationDetailsSchema),
        campaigns: z.array(campaignDetailsSchema),
        inbound: billingStatsSchema.optional(),
      })
      .optional(),
  });

export type OrderTrackingInvoice = z.infer<typeof orderTrackingInvoiceSchema>;
export const orderTrackingInvoiceSchema = baseBillingEngineInvoiceSchema.extend(
  {
    kind: z.literal(ServiceInvoiceType.ORDER_TRACKING),
    usageBillingWindowConfigId: zExt.objectId().optional(),
  },
);

export type SupportBaseInvoice = z.infer<typeof supportBaseInvoiceSchema>;
export const supportBaseInvoiceSchema = baseServiceInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.SUPPORT_BASE),
  refunded: z.boolean().optional(),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
  version: createEnum(SupportBaseBillingVersion, {
    enumName: "SupportBaseBillingVersion",
  }).default(SupportBaseBillingVersion.FLAT_RATE),
});

export type SupportAiSubscriptionInvoice = z.infer<
  typeof supportAiSubscriptionInvoiceSchema
>;
export const supportAiSubscriptionInvoiceSchema =
  baseServiceInvoiceSchema.extend({
    refunded: z.boolean().optional(),
    kind: z.literal(ServiceInvoiceType.SUPPORT_AI_SUBSCRIPTION),
    recurringInvoiceData: recurringInvoiceDataSchema.optional(),
  });

export type SupportAiUsageInvoice = z.infer<typeof supportAiUsageInvoiceSchema>;
export const supportAiUsageInvoiceSchema = baseServiceInvoiceSchema.extend({
  refunded: z.boolean().optional(),
  kind: z.literal(ServiceInvoiceType.SUPPORT_AI_USAGE),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
});

export type SupportVoiceSubscriptionInvoice = z.infer<
  typeof supportVoiceSubscriptionInvoiceSchema
>;
export const supportVoiceSubscriptionInvoiceSchema =
  baseServiceInvoiceSchema.extend({
    refunded: z.boolean().optional(),
    kind: z.literal(ServiceInvoiceType.SUPPORT_VOICE_SUBSCRIPTION),
    recurringInvoiceData: recurringInvoiceDataSchema.optional(),
  });

export type SupportVoiceUsageInvoice = z.infer<
  typeof supportVoiceUsageInvoiceSchema
>;
export const supportVoiceUsageInvoiceSchema = baseServiceInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.SUPPORT_VOICE_USAGE),
  refunded: z.boolean().optional(),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
  totalMinutesUsed: z.number(),
  pricePerMinuteInCents: z.number(),
});

export type CheckoutOptimizationInvoice = z.infer<
  typeof checkoutOptimizationInvoiceSchema
>;
export const checkoutOptimizationInvoiceSchema =
  baseServiceInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.CHECKOUT_OPTIMIZATION),
    recurringInvoiceData: recurringInvoiceDataSchema.optional(),
    ordersCreatedWithRedoShippingMethod: z.number(),
    billableOrdersCreated: z.number(),
    pricePerOrderInCents: z.number(),
  });

export type CheckoutOptimizationSubscriptionInvoice = z.infer<
  typeof checkoutOptimizationSubscriptionInvoiceSchema
>;
export const checkoutOptimizationSubscriptionInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION),
    usageBillingWindowConfigId: zExt.objectId().optional(),
  });

export type ReturnAiExchangeAgentInvoice = z.infer<
  typeof returnAiExchangeAgentInvoice
>;
export const returnAiExchangeAgentInvoice =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.RETURN_AI_EXCHANGE),
  });

export type ReturnsInvoice = z.infer<typeof returnsInvoiceSchema>;
export const returnsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.RETURNS),
});

export const serviceInvoiceSchema = z.discriminatedUnion("kind", [
  marketingSmsInvoiceSchema,
  marketingEmailInvoiceSchema,
  orderTrackingInvoiceSchema,
  supportBaseInvoiceSchema,
  supportAiSubscriptionInvoiceSchema,
  supportAiUsageInvoiceSchema,
  supportVoiceSubscriptionInvoiceSchema,
  supportVoiceUsageInvoiceSchema,
  checkoutOptimizationInvoiceSchema,
  checkoutOptimizationSubscriptionInvoiceSchema,
  returnAiExchangeAgentInvoice,
  returnsInvoiceSchema,
]);

export type ServiceInvoice = z.infer<typeof serviceInvoiceSchema>;
